// resources/js/Pages/Formula/components/SchemaImportModal.js

import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Tabs, Tab } from 'react-bootstrap';
import { useSchemaImportExport, getExampleSchemas } from './SchemaImportExport';

export default function SchemaImportModal({ 
  show, 
  onHide, 
  onImport 
}) {
  const [importJson, setImportJson] = useState('');
  const [importErrors, setImportErrors] = useState([]);
  const [activeTab, setActiveTab] = useState('import');
  
  const { processImportedJson } = useSchemaImportExport();
  const examples = getExampleSchemas();

  const handleImport = () => {
    const result = processImportedJson(importJson);
    
    if (result.success) {
      onImport(result.schema);
      handleClose();
    } else {
      setImportErrors(result.errors);
    }
  };

  const handleClose = () => {
    setImportJson('');
    setImportErrors([]);
    setActiveTab('import');
    onHide();
  };

  const loadExample = (exampleKey) => {
    const example = examples[exampleKey];
    setImportJson(JSON.stringify(example, null, 2));
    setImportErrors([]);
    setActiveTab('import');
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>📥 Import Schema</Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
// Update the warning alert in SchemaImportModal.js

<Alert variant="warning">
  <strong>⚠️ This will completely replace your current schema structure!</strong>
  <div className="small mt-1">
    Only the response structure will be replaced. Your context settings (script inclusion options) will remain unchanged.
  </div>
</Alert>


        <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-3">
          <Tab eventKey="import" title="Import JSON">
            <Form.Group className="mb-3">
              <Form.Label>Schema JSON</Form.Label>
              <Form.Control
                as="textarea"
                rows={12}
                className="font-monospace"
                placeholder="Paste your schema JSON here..."
                value={importJson}
                onChange={(e) => {
                  setImportJson(e.target.value);
                  setImportErrors([]);
                }}
                isInvalid={importErrors.length > 0}
              />
              {importErrors.length > 0 && (
                <div className="text-danger mt-2">
                  {importErrors.map((error, index) => (
                    <div key={index}><small>• {error}</small></div>
                  ))}
                </div>
              )}
            </Form.Group>

<Form.Text className="text-muted">
  Import only the schema structure (fields, lists, display types). 
  Context settings are managed separately and won't be affected.
</Form.Text>
          </Tab>
          
          <Tab eventKey="examples" title="Examples">
            <div className="mb-3">
              <h6>Schema Examples</h6>
              <p className="text-muted small">
                Click an example below to load it into the import field.
              </p>
            </div>

            <div className="mb-4">
              <h6 className="small">Simple Schema</h6>
              <p className="text-muted small mb-2">Basic fields with string and number types</p>
              <Button 
                variant="outline-primary" 
                size="sm" 
                onClick={() => loadExample('simple')}
                className="mb-2"
              >
                Load Simple Example
              </Button>
              <pre className="bg-light p-3 small rounded">
                {JSON.stringify(examples.simple, null, 2)}
              </pre>
            </div>

            <div className="mb-4">
              <h6 className="small">Table Schema</h6>
              <p className="text-muted small mb-2">Includes a table with scene numbers and ratings</p>
              <Button 
                variant="outline-primary" 
                size="sm" 
                onClick={() => loadExample('withTable')}
                className="mb-2"
              >
                Load Table Example
              </Button>
              <pre className="bg-light p-3 small rounded" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {JSON.stringify(examples.withTable, null, 2)}
              </pre>
            </div>
          </Tab>
        </Tabs>
      </Modal.Body>
      
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button 
          variant="danger" 
          onClick={handleImport}
          disabled={!importJson.trim() || activeTab !== 'import'}
        >
          Replace Schema
        </Button>
      </Modal.Footer>
    </Modal>
  );
}